import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { UserService } from "@services/UserService";
import { useFetch } from "@hooks/useFetch";
import { User } from "@models/User";
import {
  Modal,
  ModalContentWrapper,
  Button,
  DataTable,
  ConfirmDeleteModal,
} from "@components";
import { TeamColumns } from "./components/TeamMemberTableColumns";
import { AddEditTeamMember } from "./components/AddEditTeamMember";
import { AuthenticationService } from "authentication/AuthenticationService";

/**
 * Team Page
 * ---------
 * Here one can manage all team members for the distributor dashboard.
 */

export const TeamPage: React.FC = () => {
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [isAddEditTeamMemberModalOpen, setIsAddEditTeamMemberModalOpen] =
    useState(false);
  const [isDeleteTeamMemberModalOpen, setIsDeleteTeamMemberModalOpen] =
    useState(false);
  const userService = new UserService();
  const isAdmin = AuthenticationService.isAdminLikeRole();

  const {
    data: users,
    isLoading: isLoadingUsers,
    refetch: refetchUsers,
  } = useFetch(userService.getUsers, [], []);

  const openAddModal = () => {
    setSelectedUser(undefined);
    setIsAddEditTeamMemberModalOpen(true);
  };

  const onEdit = (user: User) => {
    setSelectedUser(user);
    setIsAddEditTeamMemberModalOpen(true);
  };

  const handleDeleteUser = async (user: User) => {
    try {
      await userService.deleteUser(user.id!);
      refetchUsers();
      toast.success("Team member deleted");
    } catch (error) {
      console.error("Failed to delete user", error);
      toast.error("Failed to delete user");
    }
  };

  const onDelete = (user: User) => {
    setSelectedUser(user);
    setIsDeleteTeamMemberModalOpen(true);
  };

  return (
    <div>
      <div className="flex items-end justify-between">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Team Members
          </h1>
        <div className="flex items-center gap-3">
          {isAdmin && <Button onClick={openAddModal}>Add Team Member</Button>}
        </div>
      </div>

      <DataTable
        columns={TeamColumns(onEdit, onDelete)}
        data={users || []}
        isLoading={isLoadingUsers}
      />

      <Modal
        title={selectedUser ? "Edit Team Member" : "Add Team Member"}
        isOpen={isAddEditTeamMemberModalOpen}
        onClose={() => setIsAddEditTeamMemberModalOpen(false)}
        closeWhenClickOutside={false}
      >
        <ModalContentWrapper>
          <AddEditTeamMember
            user={selectedUser}
            onClose={() => setIsAddEditTeamMemberModalOpen(false)}
            onUserAdded={() => {
              refetchUsers();
              toast.success("Team member added");
            }}
            onUserUpdated={() => {
              refetchUsers();
              toast.success("Team member updated");
            }}
          />
        </ModalContentWrapper>
      </Modal>
      <ConfirmDeleteModal
        isOpen={isDeleteTeamMemberModalOpen}
        onClose={() => setIsDeleteTeamMemberModalOpen(false)}
        onConfirm={() => {
          handleDeleteUser(selectedUser!);
          setIsDeleteTeamMemberModalOpen(false);
        }}
        title="Delete Team Member"
        description="Are you sure you want to delete this team member?"
        itemToDelete={selectedUser}
        itemName={
          selectedUser
            ? `${selectedUser.firstName} ${selectedUser.lastName}`
            : undefined
        }
      />
    </div>
  );
};
